import { createContext, useContext } from 'react';
import { ActionCreatorsType, ActionsType, ActionType, AxiosResponseType, ReducerType } from 'types';
import { AlertState, CloseAlertPayload } from './types';

const CONTEXT_NAME = 'globalStateContext';

export const initialState = {
  alertDetails: [] as AlertState[],
  tenantLogo: '',
  action: '',
};

const handlers = {
  openAlert: (state: typeof initialState, payload: AlertState) => {
    return {
      ...state,
      alertDetails: state.alertDetails.concat(payload),
    };
  },
  closeAlert: (state: typeof initialState, payload: CloseAlertPayload) => {
    const stateClone: typeof initialState = structuredClone(state);
    const alertDetails = (stateClone.alertDetails as AlertState[]).find((alert: AlertState) => alert.id === payload.id);

    if (alertDetails) {
      alertDetails.isOpen = false;
    }

    return stateClone;
  },
  clearAlert: (state: typeof initialState, payload: CloseAlertPayload) => ({
    ...state,
    alertDetails: state.alertDetails.filter((alert) => {
      return alert.id !== payload.id;
    }),
  }),
  setTenantLogo: (state: typeof initialState, payload: string) => ({
    ...state,
    tenantLogo: payload,
  }),
  setAction: (state: typeof initialState, payload: string) => ({
    ...state,
    action: payload,
  }),
};


const ActionCreatorsFactory = <T, U>(
  settings: { moduleName: string },
  actions: { [key: string]: AxiosResponseType },
): ActionCreatorsType<T, U> => {
  const { moduleName } = settings;

  const result: AxiosResponseType = {};

  Object.keys(actions).forEach((actionName) => {
    const type = `${moduleName}/${actionName}`;

    result[actionName] = (payload?: AxiosResponseType) => ({ type, payload });
    result[actionName].type = type;
    result[type] = (state: T, payload?: AxiosResponseType) => actions[actionName](state, payload);
  });

  return result;
};

export const actions = ActionCreatorsFactory({ moduleName: CONTEXT_NAME }, handlers);

const ReducerFactory =
  <T>(initialState: T, actions: ActionsType<T>): ReducerType<T> =>
  (state = initialState, action = { type: '' }): T => {
    if (typeof actions[action.type] === 'function') {
      return actions[action.type](state, action.payload) || state;
    }
    return state;
  }


export const reducer = ReducerFactory(initialState, actions);

export const GlobalStateContext = createContext({
  state: initialState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dispatch: (action: ActionType<unknown>) => {
    //
  },
});

export const useGlobalState = () => useContext(GlobalStateContext);
